import L from "leaflet";
import { useMap } from "react-leaflet";
import { GetData } from "../lib/DataRetrieval";
import { UrlToQuery } from "../lib/Endpoints";
import { mapColorScaleOccupancy, mapColorNoData, mapColorEvent } from "../lib/ColorPalette";
import { useState, useEffect } from 'react'
import { getTodayDate, utcToLocalString } from "../lib/DateToday";
import EventsOverlay from "../components/EventsOverlay";

const drawnItems = new L.GeoJSON();

export default function ParkingLots(props) {
    const init = new Date()
    const [date, setDate] = useState(init)
    const [extraInfo, setExtraInfo] = useState(null);

    const data = GetData(UrlToQuery("parkingLots"));
    const status = GetData(UrlToQuery("parkingStatus"));
    const events = GetData(UrlToQuery("events"));
    const map = useMap();

    useEffect(() => {
        fetch("/extraParkingInfo.json")
            .then(res => res.json())
            .then(setExtraInfo)
            .catch(() => setExtraInfo(null));
    }, []);

    const tick = () => {
        setDate(new Date())
    }

    useEffect(() => {
        const timerID = setInterval(() => tick(), 120000)
        return () => {
            clearInterval(timerID)
        }
    }, [])

    if (data != null && status != null) {

        map.removeLayer(drawnItems);
        drawnItems.clearLayers();
        map.addLayer(drawnItems);

        for (let i = 0; i < data.length; i++) {
            let obj = data[i];

            if (!obj.geo) {
                console.log(obj.name + " has no associated geometry")
                continue;
            }

            // Find extra info for this parking lot
            let extra = null;
            if (extraInfo && obj.id != null) {
                let exstaMatch = extraInfo.find(e => e.id === obj.id);
                extra = exstaMatch || null;
            }

            let polygon
            let handicapSpace = '';
            if (obj.spaceTypes?.handicap) {
                handicapSpace = `${obj.spaceTypes.handicap}`;
            }
            let commercialSpace = '';
            if (obj.spaceTypes?.commercial) {
                commercialSpace = `${obj.spaceTypes.commercial}`;
            }
            let electricSpace = "";
            if (obj.spaceTypes["el Car"]) {
                electricSpace = `${obj.spaceTypes["el Car"]}`;
            }
            let timeConstraint = '';
            if (obj.restrictions.timeConstraint !== 0) {
                let totalMinutes = obj.restrictions.timeConstraint;
                let hours = Math.floor(totalMinutes / 60);
                let minutes = totalMinutes % 60;

                if (hours > 0) {
                    timeConstraint += hours + ' timer';
                }
                if (minutes > 0) {
                    if (hours > 0) {
                        timeConstraint += ' ';
                    }
                    timeConstraint += minutes + ' minutter';
                }
            }
            let freeSpaces = null;
            const statObj = status.find(s => s.id === obj.id);
            if (statObj && typeof statObj.status === 'number' && obj.hasLiveData) {
                freeSpaces = Math.round(obj.spaceCount * statObj.status);
            }

            let parkingCost = '';
            let weekdaysFrom = '';
            let weekdaysTo = '';
            let saturdaysFrom = '';
            let saturdaysTo = '';
            let sundaysFrom = '';
            let sundaysTo = '';
            let restrictionLabel = '';

            if (obj.restrictions?.cost && obj.restrictions.cost.trim() !== '') {
                restrictionLabel = '';
                parkingCost = `kr. ${obj.restrictions.cost} pr/time`;
                if (obj.restrictions.weekday.from !== '' && obj.restrictions.weekday.to !== '') {
                    weekdaysFrom = `Hverdage: ${obj.restrictions.weekday.from} `;
                    weekdaysTo = `- ${obj.restrictions.weekday.to}`;
                } else {
                    weekdaysFrom = "Hverdage: Gratis <br />";
                }
                if (obj.restrictions.saturday.from !== '' && obj.restrictions.to !== '') {
                    saturdaysFrom = `Lørdag: ${obj.restrictions.saturday.from} `;
                    saturdaysTo = `- ${obj.restrictions.saturday.to}`;
                } else {
                    saturdaysTo = "Lørdage: Gratis <br />";
                }
                if (obj.restrictions.sunday.from !== '' && obj.restrictions.to !== '') {
                    sundaysFrom = `Søndag: ${obj.restrictions.sunday.from} `;
                    sundaysTo = `- ${obj.restrictions.sunday.to}`;
                } else {
                    sundaysTo = "Søndage: Gratis <br />";
                }
            } 
            else if (obj.restrictions?.hasTimeConstraint === true) {
                restrictionLabel = '';
                if (obj.restrictions.weekday.from !== '' && obj.restrictions.weekday.to !== '') {
                    weekdaysFrom = `Hverdage: ${obj.restrictions.weekday.from} `;
                    weekdaysTo = `- ${obj.restrictions.weekday.to}`;
                } else {
                    weekdaysFrom = "";
                }
                if (obj.restrictions.saturday.from !== '' && obj.restrictions.to !== '') {
                    saturdaysFrom = `Lørdag: ${obj.restrictions.saturday.from} `;
                    saturdaysTo = `- ${obj.restrictions.saturday.to}`;
                } else {
                    saturdaysTo = "";
                }
                if (obj.restrictions.sunday.from !== '' && obj.restrictions.to !== '') {
                    sundaysFrom = `Søndag: ${obj.restrictions.sunday.from} `;
                    sundaysTo = `- ${obj.restrictions.sunday.to}`;
                } else {
                    sundaysTo = "";
                }
            }
            else {
                parkingCost = "Gratis <br />";
            }

            let color = mapColorNoData();
            if (events != null) {
              const today = new Date(getTodayDate());
              const activeEvent = events.find(e => 
                e.parkingLotId === obj.id &&
                new Date(e.startTime) <= today &&
                new Date(e.endTime) >= today
              );
              if (activeEvent) {
                  color = mapColorEvent();
              }
            }
            if (color === mapColorNoData() && obj.hasLiveData && status != null) {
              for (let i = 0; i < status.length; i++) {
                let statObj = status[i];
                if (obj.id === statObj.id) {
                    color = mapColorScaleOccupancy(statObj.status);
                    break;
                }
              }
            }

            polygon = L.polygon(obj.geo, {
                color: color,
                opacity: 1,
                fillOpacity: 0.5,
                weight: 5
            });

            // popup content generation
            let popupHtml = `
                <div class="polygon-popup-header">${obj.name}</div>`


            let address = obj.address;
            if (address !== null && address.trim() !== '') {
                popupHtml += `<div class="polygon-popup-content-box">
                    <i class="material-icons">&#xe55f;</i>
                    <div class="polygon-popup-content-left-text">Adresse</div>
                    <div class="polygon-popup-content-right-text">${obj.address}</div>
                </div>`
            }
            
            const nowUtcString = getTodayDate();
            const nowUtc = new Date(nowUtcString);

            const activeEvent = events?.find(e =>
                e.parkingLotId === obj.id &&
                new Date(e.startTime) <= nowUtc &&
                new Date(e.endTime) >= nowUtc
            );

            if (!activeEvent) {
                popupHtml += `<div class="polygon-popup-content-box">
                    <i class="material-icons">&#xe54f;</i>
                    <div class="polygon-popup-content-left-text">Pladser i alt</div>
                    <div class="polygon-popup-content-right-text">${obj.spaceCount}</div>
                </div>`;

                if (freeSpaces !== null) {
                    popupHtml += `<div class="polygon-popup-content-box">
                        <i class="material-icons">&#xe565;</i>
                        <div class="polygon-popup-content-left-text">Ledige pladser</div>
                        <div class="polygon-popup-content-right-text">${freeSpaces}</div>
                    </div>`;
                }

                if (parkingCost?.trim()) { 
                    popupHtml += `<div class="polygon-popup-content-box" style="flex-direction: column; align-items: stretch;">
                        <div class="time-constraint-header">
                            <i class="material-icons">&#xf041;</i>
                            <div class="polygon-popup-content-left-text">Pris</div>
                            <div class="polygon-popup-content-right-text">${parkingCost}</div>
                        </div>`;
                    if (weekdaysFrom || weekdaysTo) popupHtml += `
                        <div class="time-constraint-values">
                            <div class="polygon-popup-content-left-text">${restrictionLabel}</div>
                            <div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${weekdaysFrom}${weekdaysTo}</div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${saturdaysFrom}${saturdaysTo}</div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${sundaysFrom}${sundaysTo}</div>
                            </div>
                        </div>
                    </div>`;
                } else if (timeConstraint?.trim()) {
                    popupHtml += `<div class="polygon-popup-content-box" style="flex-direction: column; align-items: stretch;">
                        <div class="time-constraint-header">
                            <i class="material-icons">&#xe923;</i>
                            <div class="polygon-popup-content-left-text">Tidsbegrænsning</div>
                            <div class="polygon-popup-content-right-text">${timeConstraint}</div>
                        </div>`;
                    if (weekdaysFrom || weekdaysTo) popupHtml += `
                        <div class="time-constraint-values">
                            <div class="polygon-popup-content-left-text">${restrictionLabel}</div>
                            <div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${weekdaysFrom}${weekdaysTo}</div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${saturdaysFrom}${saturdaysTo}</div>
                                <div class="polygon-popup-content-right-text" style="font-weight: lighter !important">${sundaysFrom}${sundaysTo}</div>
                            </div>
                        </div>
                    </div>`;
                }
                popupHtml += `</div>`; // Denne gør at divs ikke er bugged ind i en anden boks

                if (handicapSpace?.trim()) popupHtml += `<div class="polygon-popup-content-box">
                    <i class="material-icons">&#xe914;</i>
                    <div class="polygon-popup-content-left-text">Handicappladser</div>
                    <div class="polygon-popup-content-right-text">${handicapSpace}</div>
                </div>`;
                if (commercialSpace?.trim()) popupHtml += `<div class="polygon-popup-content-box">
                    <i class="material-icons">&#xe531;</i>
                    <div class="polygon-popup-content-left-text">Commercial pladser</div>
                    <div class="polygon-popup-content-right-text">${commercialSpace}</div>
                </div>`;
                if (electricSpace?.trim()) popupHtml += `<div class="polygon-popup-content-box">
                    <i class="material-icons">&#xe3e7;</i>
                    <div class="polygon-popup-content-left-text">El pladser</div>
                    <div class="polygon-popup-content-right-text">${electricSpace}</div>
                </div>`;


                if (extra !== null) {
                    popupHtml += `<div class="polygon-popup-content-box">
                        <i class="material-icons">&#xe88e;</i>
                        <div class="polygon-popup-content-left-text">Ekstra information</div>
                        <div class="polygon-popup-content-right-text">${extra.info}</div>
                    </div>`;
                }
            } else {
                popupHtml += `
                    <div class="polygon-popup-content-box">
                        <i class="material-icons">&#xe88e;</i>
                        <div class="polygon-popup-content-left-text">Event</div>
                        <div class="polygon-popup-content-right-text">
                            <div class="time-row" style="display:inline-block; width:140px;">
                                <span class="time-label">Start:</span>
                                <span class="time-value">${utcToLocalString(activeEvent.startTime)}</span>
                            </div>
                            <div class="time-row" style="display:inline-block; width:140px;">
                                <span class="time-label">Slut:</span>
                                <span class="time-value">${utcToLocalString(activeEvent.endTime)}</span>
                            </div>
                            <br /><br />
                            <div style="display:inline-block; width:160px;">
                            ${activeEvent.description["da"]}
                            </div>
                        </div>
                    </div>
                    `;
            }
            polygon.bindPopup(popupHtml, {
                closeButton: false,
                maxWidth: 'auto'
            });
            drawnItems.addLayer(polygon);
        }

        return (
            <>
                {Array.isArray(events) && events.length > 0 && (
                <EventsOverlay events={events} lots={data ?? []} lang="da" /> // vælg sprog
                )}
            </>
        );
    }

    else {
        return null;
    }
}
